/**
 * useProfile Hook
 * 
 * Handles profile data and updates.
 * Encapsulates API calls per Rule 6.
 */

import { useState, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { useAuthStore } from '../store/authStore';
import { logger } from '../shared/utils/logger';

interface ProfileData {
  display_name: string;
}

interface UseProfileReturn {
  isLoading: boolean;
  error: string | null;
  updateProfile: (data: ProfileData) => Promise<boolean>;
}

export function useProfile(): UseProfileReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshUser = useAuthStore((state) => state.refreshUser);

  const updateProfile = useCallback(async (data: ProfileData): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.updateProfile(data);
      
      if (response.success) {
        // Refresh the user in the auth store to get updated data
        await refreshUser();
        return true;
      } else {
        setError('Failed to update profile');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      logger.error('Profile update failed', 'profile', String(err));
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [refreshUser]);

  return {
    isLoading,
    error,
    updateProfile,
  };
}

